import { RECYCLE_PLAYLIST_KEYS, type RecycleEvent } from '@dooh/shared';

/**
 * Pure overlay state machine for the recycle-kiosk experience. Maps agent WS
 * events (see packages/shared/src/recycle-contract.ts — the CONTRACT) to the
 * overlay screen the player should render. No side effects, no timers: the
 * component layer (Task 6) owns auto-return-to-idle timers and calls
 * `resetToIdle` when they fire.
 */
export type RecycleOverlayState =
  | 'idle'
  | 'processing'
  | 'deposit_item'
  | 'product'
  | 'no_match'
  | 'barcode_not_qr'
  | 'no_item_detected'
  | 'obstructed'
  | 'restart_required'
  // v2 additive: kiosk-unusable full-screen (bin full or required hardware
  // unhealthy), driven entirely by agent_status — see the reducer's
  // 'agent_status' case.
  | 'out_of_service'
  // v2 additive: brief, self-clearing notice for a fail-closed scan_rejected
  // (no transaction was ever created). The overlay (Task 6) owns dismissal —
  // this machine has no timers of its own.
  | 'rejected';

/** v2 additive: reasons the kiosk reports as unable to accept a scan/service. */
export type RecycleOutOfServiceReason = 'bin_full' | 'hardware_fault';

/** v2 additive: product display block carried on an accepted verdict. */
export interface RecycleProductInfo {
  name: string;
  brand?: string;
  category?: string;
  imageUrl?: string;
}

export interface RecycleContext {
  state: RecycleOverlayState;
  txnId: string | null;
  barcode: string | null;
  playlistOverride: string | null;
  /** qrUrl from the legacy `reward` event. */
  rewardUrl: string | null;
  obstructionAttempt: number;
  /** v2 additive: reason carried by the active out_of_service screen, if any. */
  outOfServiceReason: RecycleOutOfServiceReason | null;
  /** v2 additive: reason carried by the active transient rejected notice, if any. */
  rejectedReason: RecycleOutOfServiceReason | null;
  /** v2 additive: transaction-specific reward QR from the verdict event. */
  rewardQrUrl: string | null;
  /** v2 additive: product display info from the verdict event, if provided. */
  product: RecycleProductInfo | null;
}

export const INITIAL_RECYCLE_CONTEXT: RecycleContext = {
  state: 'idle',
  txnId: null,
  barcode: null,
  playlistOverride: null,
  rewardUrl: null,
  obstructionAttempt: 0,
  outOfServiceReason: null,
  rejectedReason: null,
  rewardQrUrl: null,
  product: null,
};

/** Caller-driven return to idle (e.g. after a screen's auto-dismiss timer). */
export function resetToIdle(_ctx: RecycleContext): RecycleContext {
  return { ...INITIAL_RECYCLE_CONTEXT };
}

export function isOverlayVisible(state: RecycleOverlayState): boolean {
  return state !== 'idle';
}

const KEY_BY_STATE: Partial<Record<RecycleOverlayState, string>> = {
  processing: RECYCLE_PLAYLIST_KEYS.processing,
  deposit_item: RECYCLE_PLAYLIST_KEYS.deposit_item,
  no_match: RECYCLE_PLAYLIST_KEYS.no_match,
  barcode_not_qr: RECYCLE_PLAYLIST_KEYS.barcode_not_qr,
  no_item_detected: RECYCLE_PLAYLIST_KEYS.no_item_detected,
  product: RECYCLE_PLAYLIST_KEYS.product,
};

/** Reserved manifest playlist key for the given overlay screen, or null for
 * screens with no themable media (idle, obstructed, restart_required). */
export function playlistKeyForState(state: RecycleOverlayState): string | null {
  return KEY_BY_STATE[state] ?? null;
}

export function recycleReducer(ctx: RecycleContext, event: RecycleEvent): RecycleContext {
  // Stale-txnId guard: reject transactional events from old/unknown transactions.
  // Rationale: late events after idle reset or from a previous transaction would
  // corrupt overlay state. scan and agent_status bypass this guard (scan starts
  // a new txn, agent_status is non-transactional).
  if (event.type !== 'scan' && event.type !== 'agent_status') {
    if (event.txnId !== null && (ctx.txnId === null || event.txnId !== ctx.txnId)) {
      return ctx; // stale or unknown transaction
    }
  }

  switch (event.type) {
    case 'agent_status': {
      // v2 additive: health/connection is otherwise handled by the WS hook,
      // not the overlay state machine — but OUT_OF_SERVICE is kiosk-unusable
      // and must take over the overlay regardless of what's currently
      // in-flight (fail-closed: the agent itself refuses scans while this
      // holds). A later agent_status carrying a different explicit `state`
      // is the agent's own signal that service resumed, so it clears back to
      // idle. Bare/partial health ticks (no `state` field) are a no-op.
      const nextAgentState = event.data.state;
      if (nextAgentState === 'OUT_OF_SERVICE') {
        return {
          ...INITIAL_RECYCLE_CONTEXT,
          state: 'out_of_service',
          outOfServiceReason: event.data.outOfServiceReason ?? null,
        };
      }
      if (ctx.state === 'out_of_service' && nextAgentState !== undefined) {
        return { ...INITIAL_RECYCLE_CONTEXT };
      }
      return ctx;
    }
    case 'scan':
      return {
        ...INITIAL_RECYCLE_CONTEXT,
        state: 'processing',
        txnId: event.txnId,
        barcode: event.data.barcode,
      };
    case 'awaiting_verdict':
      return { ...ctx, state: 'processing', txnId: event.txnId };
    case 'verdict':
      return event.data.recyclable
        ? {
            ...ctx,
            state: 'deposit_item',
            playlistOverride: event.data.playlistOverride,
            // v2 additive: DOOH-owned reward + product display, carried
            // straight from the verdict rather than a later `reward` event.
            rewardQrUrl: event.data.rewardQrUrl,
            product: event.data.product ?? null,
          }
        : { ...ctx, state: 'no_match' };
    case 'deposit_started':
      return { ...ctx, state: 'deposit_item' };
    case 'result':
      // CONTRACT: result.data is { outcome: 'success' | 'no_item' }, not the
      // brief's original { success: boolean } sketch.
      return { ...ctx, state: event.data.outcome === 'success' ? 'product' : 'no_item_detected' };
    case 'no_match':
      return { ...ctx, state: 'no_match' };
    case 'qr_reject':
      return { ...ctx, state: 'barcode_not_qr' };
    case 'reward':
      return { ...ctx, rewardUrl: event.data.qrUrl };
    case 'obstructed':
      return { ...ctx, state: 'obstructed', obstructionAttempt: event.data.attempt };
    case 'restart_required':
      return { ...ctx, state: 'restart_required' };
    case 'verdict_timeout':
      return { ...ctx, state: 'no_match' };
    // v2 additive: emitted exactly once after a successful obstruction clear
    // (whether the screen had reached the escalated restart_required or was
    // still plain obstructed) — replaces the old "silently reset to idle
    // with no terminal event" gap. Unsticks the overlay back to idle; the
    // pending OBSTRUCTED interaction is flushed by the idle-transition
    // outcome-tracking effect (see interaction-outcomes.ts), same as any
    // other reset to idle.
    case 'recovery_complete':
      return resetToIdle(ctx);
    // v2 additive: fail-closed scan gating refused the scan before any
    // transaction was created (txnId is always null on this event) — a
    // brief, self-clearing notice. No timer here (the machine is pure); the
    // overlay owns the dwell/dismiss timer, same pattern as the other
    // terminal-but-transient screens.
    case 'scan_rejected':
      return { ...INITIAL_RECYCLE_CONTEXT, state: 'rejected', rejectedReason: event.data.reason };
    default:
      return ctx;
  }
}
